import { StrictOmit } from "../../lib/lang"
import { UserType } from "../login/loginTypes"

export enum ModuleName {
    CATERING = "CATERING",
    BOOKING = "BOOKING",
    TAKE_AWAY = "TAKE_AWAY",
    REFILL_WALLET = "REFILL_WALLET",
    APP_PAYMENT = "APP_PAYMENT",
    POS_PAYMENT = "POS_PAYMENT",
}

export type Module = {
    name: ModuleName
    isActive: boolean
    description: string
}

// --- New company-user contract (split settings) ---

export enum UserRole {
    SECRETARY = "secretary",
}

export enum UserEventType {
    EMAIL_INVITATION_SENT = "EMAIL_INVITATION_SENT",
    LOGGED_IN = "LOGGED_IN",
}

export enum UserActionType {
    SEND_EMAIL_INVITATION = "SEND_EMAIL_INVITATION",
}

export type UserAction = Readonly<{ type: UserActionType }>

export type UserEvent = Readonly<{
    timestamp: string
    eventType: UserEventType
}>

export type UserLogins = Readonly<{
    loginCount: number
    lastLogin?: string
    events: ReadonlyArray<UserEvent>
}>

export type CompanySetup = Readonly<{
    emailReceipts?: boolean
    userSynchronization: boolean
}>

export type UserCompany = Readonly<{
    id: number
    uid?: string
    name?: string
    companySetup: CompanySetup
}>

export type UserNotificationSettings = Readonly<{
    notifyOnGuestArrival: boolean
}>

export type PriceListRef = Readonly<{
    id: number
    name: string
}>

export enum AdminRole {
    agreement_owner = "agreement_owner",
    dpo = "dpo",
    it_administrator = "it_administrator",
    bookkeeper = "bookkeeper",
    news_subscriber = "news_subscriber",
    system_operation_subscriber = "system_operation_subscriber",
}

type HistoryRecord = {
    timestamp: string
    author: string
}

export type History = {
    created: HistoryRecord
    updated: HistoryRecord
}

export type UserSettings = {
    ignoreUserSyncJobs: boolean
}

export enum UserSettingElementType {
    TOGGLE = "TOGGLE",
    DROP_DOWN = "DROP_DOWN",
}

export type UserSetting = Readonly<{
    name: string
    translationKey?: string
    elementType: UserSettingElementType
    value: string
}>

export type AdminSecuritySettings = {
    allowToCreateAdmins?: boolean
    restrictAccessToIpAddresses?: string
    pinCode?: { value: string }
}
// AdminAdministratorFor
export type AdminAdministratorForUserGroup = {
    id: number
    name: string
}

export type AdminAdministratorForCompany = {
    id: number
    name: string
    isOfficeHotel: boolean
}
export type AdminAdministratorFor = Readonly<{
    company?: AdminAdministratorForCompany
    userGroup: AdminAdministratorForUserGroup
}>
// END AdminAdministratorFor

export type User = Readonly<{
    id: number
    companyId?: number
    customerTypeId?: number // this is actually the user group of the user (employee, consultant, etc) not a user type as such
    uid: string
    username: string
    initials?: string
    displayInitials?: string

    employeeId?: string
    displayName: string
    email: string
    mobileNumber?: string
    phoneNumber?: string
    department?: Department
    userGroup?: UserGroupType
    isActive: boolean
    isPrivateAccount?: boolean
    isTest?: boolean

    // --- New company-user contract fields (split settings) ---
    language?: string
    company?: UserCompany
    roles?: ReadonlyArray<string>
    approverForUserGroups?: ReadonlyArray<UserGroupType>
    assignedPriceLists?: ReadonlyArray<PriceListRef>
    notificationSettings?: UserNotificationSettings
    lastLoginEvent?: UserEvent

    settings?: UserSettings
    history?: History

    userSettings?: UserSetting[]
    uidHash?: string

    workgroups?: ReadonlyArray<UserWorkgroup>
    modules?: Module[]
    location?: {
        id?: number
        kitchen?: {
            id: number
        }
        selectLocation?: boolean
    }
}>

export type CompaniesUsersItem = User & {
    initials: string
    surname: string
    givenName: string
    language: string
}

export type CreateUser = StrictOmit<
    User,
    "id" | "uid" | "username" | "department" | "isActive" | "isTest" | "settings" | "history" | "workgroups"
> &
    Readonly<{
        username?: string
        department?: {
            id: number
        }
        settings?: UserSettings &
            Readonly<{
                sendEmailInvitation?: boolean
            }>
        isTest?: boolean
        workgroups?: ReadonlyArray<{ id: number }>
    }>

export type UpdateUser = StrictOmit<
    User,
    "id" | "uid" | "username" | "department" | "isPrivateAccount" | "isTest" | "history" | "workgroups"
> &
    Readonly<{
        username?: string
        department?: {
            id: number
        }
        workgroups?: ReadonlyArray<{ id: number }>
    }>

export type AdminUser = StrictOmit<
    User,
    | "companyId"
    | "customerTypeId"
    | "employeeId"
    // | "phoneNumber"
    | "department"
    | "isPrivateAccount"
    | "isTest"
    | "settings"
    | "history"
>

export type CreateAdminUser = StrictOmit<AdminUser, "id" | "uid" | "username" | "isActive"> &
    Readonly<{
        username?: string
    }>

export type UpdateAdminUser = StrictOmit<AdminUser, "id" | "uid" | "username"> &
    Readonly<{
        username?: string
    }>

type BaseAdmin = {
    user: AdminUser
    roles?: AdminRole[]
    securitySettings?: AdminSecuritySettings
    history?: History
    administratorFor?: AdminAdministratorFor
}

export type OfficeManagerDetails = {
    companyId: number
    userGroupId: number
}

export type TenantOfficeManager = BaseAdmin &
    Readonly<{
        type: UserType.OFFICE_MANAGER
        officeManagerDetails: OfficeManagerDetails
    }>

export type CompanyAdminDetails = {
    companyId: number
}

export type CompanyAdmin = BaseAdmin &
    Readonly<{
        type: UserType.COMPANY
        companyDetails: CompanyAdminDetails
    }>

export type MerchantAdminDetails = {
    companyId: number
    /** Read-only: returned by the API, never sent when creating or updating an administrator. */
    companyName?: string
}

export type MerchantAdmin = BaseAdmin &
    Readonly<{
        type: UserType.MERCHANT
        merchantDetails: MerchantAdminDetails
    }>

export type SupplierAdminDetails = {
    companyId: number
}

export type SupplierAdmin = BaseAdmin &
    Readonly<{
        type: UserType.SUPPLIER
        supplierDetails: SupplierAdminDetails
    }>

export type SupportAdmin = BaseAdmin &
    Readonly<{
        type: UserType.SUPPORT
    }>

export type ServiceCenterAdmin = BaseAdmin &
    Readonly<{
        type: UserType.SERVICE_CENTER
    }>

export type Department = Readonly<{
    id: number
    number: string
    name: string
}>

export type UserGroupSyncDetails = Readonly<{
    source: string
    lastSynced: string
}>

export type UserGroupType = Readonly<{
    id: number
    name: string
    syncDetails?: UserGroupSyncDetails
}>

export type UserWorkgroup = Readonly<{
    id: number
    name: string
}>

export type AdminDetails = OfficeManagerDetails | CompanyAdminDetails | MerchantAdminDetails | SupplierAdminDetails

export type Admin =
    | TenantOfficeManager
    | CompanyAdmin
    | MerchantAdmin
    | SupplierAdmin
    | SupportAdmin
    | ServiceCenterAdmin

// NOTE: TypeScript cannot (yet) handle derived types from discriminated unions (discrimination didn't work), so we
// need to recreate the union here, sigh...
export type CreateTenantAdmin = StrictOmit<TenantOfficeManager, "user" | "history"> &
    Readonly<{
        user: CreateAdminUser
    }>
export type CreateCompanyAdmin = StrictOmit<CompanyAdmin, "user" | "history"> &
    Readonly<{
        user: CreateAdminUser
    }>
export type CreateMerchantAdmin = StrictOmit<MerchantAdmin, "user" | "history"> &
    Readonly<{
        user: CreateAdminUser
    }>
export type CreateSupplierAdmin = StrictOmit<SupplierAdmin, "user" | "history"> &
    Readonly<{
        user: CreateAdminUser
    }>
export type CreateSupportAdmin = StrictOmit<SupportAdmin, "user" | "history"> &
    Readonly<{
        user: CreateAdminUser
    }>

export type CreateServiceCenterAdmin = StrictOmit<ServiceCenterAdmin, "user" | "history"> &
    Readonly<{
        user: CreateAdminUser
    }>

export type CreateAdmin =
    | CreateTenantAdmin
    | CreateCompanyAdmin
    | CreateMerchantAdmin
    | CreateSupplierAdmin
    | CreateSupportAdmin
    | CreateServiceCenterAdmin

export type UpdateTenantAdmin = StrictOmit<TenantOfficeManager, "user" | "history"> &
    Readonly<{
        user: UpdateAdminUser
    }>
export type UpdateCompanyAdmin = StrictOmit<CompanyAdmin, "user" | "history"> &
    Readonly<{
        user: UpdateAdminUser
    }>
export type UpdateMerchantAdmin = StrictOmit<MerchantAdmin, "user" | "history"> &
    Readonly<{
        user: UpdateAdminUser
    }>
export type UpdateSupplierAdmin = StrictOmit<SupplierAdmin, "user" | "history"> &
    Readonly<{
        user: UpdateAdminUser
    }>
export type UpdateSupportAdmin = StrictOmit<SupportAdmin, "user" | "history"> &
    Readonly<{
        user: UpdateAdminUser
    }>

export type UpdateServiceCenterAdmin = StrictOmit<ServiceCenterAdmin, "user" | "history"> &
    Readonly<{
        user: UpdateAdminUser
    }>

export type UpdateAdmin =
    | UpdateTenantAdmin
    | UpdateCompanyAdmin
    | UpdateMerchantAdmin
    | UpdateSupplierAdmin
    | UpdateSupportAdmin
    | UpdateServiceCenterAdmin

export function isTenantUser(user: User | Admin | undefined): user is User {
    return user !== undefined && "displayName" in user && user.displayName !== undefined
}

export function isAdmin(user: User | Admin | undefined): user is Admin {
    return user !== undefined && "type" in user && user.type !== undefined
}

export type UserState = Readonly<{
    user: User | undefined
}>
